/** Uebersicht aller Setlists. */
import type { View } from '../app.ts';
import { navigate, refresh } from '../app.ts';
import { deleteSetlist, emptySetlist, listSetlists, saveSetlist } from '../db/repo.ts';
import { activeProject } from '../db/active.ts';
import { button, h } from '../ui/dom.ts';
import { toast } from '../ui/toast.ts';
import { SEPARATOR, withContextMenu } from '../ui/context-menu.ts';
import { exportSetlist } from '../features/pdf/dialog.ts';

export async function renderSetlists(): Promise<View> {
  const project = await activeProject();
  if (!project) {
    navigate('#/projects', true);
    return { title: 'Projekte', body: h('div'), drawing: null };
  }
  // Id festhalten: in hochgezogenen Funktionen greift die Verengung sonst nicht.
  const projectId = project.id;
  const setlists = await listSetlists(project.id);

  const listEl = h('ul', { class: 'list' });

  if (setlists.length === 0) {
    listEl.append(
      h(
        'li',
        { class: 'empty' },
        h('p', { class: 'empty__title', text: 'Noch keine Setlists' }),
        h('p', { text: 'Eine Setlist ist eine geordnete Liste deiner Songs.' }),
      ),
    );
  }

  for (const setlist of setlists) {
    listEl.append(
      withContextMenu(h(
        'li',
        { class: 'list-item' },
        h(
          'button',
          {
            class: 'list-item__main',
            attrs: { type: 'button' },
            on: { click: () => navigate(`#/setlist/${setlist.id}`) },
          },
          h('span', { class: 'list-item__title', text: setlist.name || 'Ohne Namen' }),
          h('span', {
            class: 'list-item__meta',
            text: `${setlist.songIds.length} ${setlist.songIds.length === 1 ? 'Song' : 'Songs'}`,
          }),
        ),
        h(
          'div',
          { class: 'list-item__actions' },
          setlist.songIds.length > 0
            ? button('▶', () => navigate(`#/stage/setlist/${setlist.id}/0`), {
                class: 'btn btn--ghost btn--icon',
                title: 'Bühnen-Modus starten',
              })
            : null,
          button(
            'Löschen',
            async () => {
              if (!confirm(`Setlist "${setlist.name || 'Ohne Namen'}" löschen?`)) return;
              await deleteSetlist(setlist.id);
              toast('Setlist gelöscht');
              refresh();
            },
            { class: 'btn btn--ghost btn--small', title: 'Setlist löschen' },
          ),
        ),
      ), setlist.name || 'Ohne Namen', () => [
        { label: 'Öffnen', action: () => navigate(`#/setlist/${setlist.id}`) },
        ...(setlist.songIds.length > 0
          ? [
              { label: 'Bühnen-Modus starten', action: () => navigate(`#/stage/setlist/${setlist.id}/0`) },
              { label: 'Als PDF …', action: () => void exportSetlist(setlist) },
            ]
          : []),
        SEPARATOR,
        {
          label: 'Löschen …',
          danger: true,
          action: async () => {
            if (!confirm(`Setlist "${setlist.name || 'Ohne Namen'}" löschen?`)) return;
            await deleteSetlist(setlist.id);
            toast('Setlist gelöscht');
            refresh();
          },
        },
      ]),
    );
  }

  async function create(): Promise<void> {
    const setlist = await saveSetlist(emptySetlist(projectId, { name: 'Neue Setlist' }));
    navigate(`#/setlist/${setlist.id}`);
  }

  const body = h(
    'div',
    {},
    listEl,
    h('button', {
      class: 'fab',
      text: '+ Neu',
      attrs: { type: 'button' },
      on: { click: () => void create() },
    }),
  );

  return {
    title: 'Setlists',
    subtitle: project.name || 'Ohne Namen',
    body,
  };
}
